import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, Pressable, Animated } from 'react-native';
import FontFamily from '../../../../constants/fonts';
import { useTheme } from '../../../../theme/useTheme';
import { KfaStats, ObsStats, RackingStats, NhoStats, SiteEvalStats } from '../../../../store/slices/adminSlice';

interface ModuleStat {
  id: string;
  label: string;
  count: number;
  subtitle: string;
  bg: string;
  labelColor: string;
  countColor: string;
  subtitleColor: string;
  activeBg?: string;
}

interface KfaModuleSummaryCardsProps {
  onModuleSelect?: (moduleId: string) => void;
  stats?: KfaStats;
  obsStats?: ObsStats;
  rackingStats?: RackingStats;
  nhoStats?: NhoStats;
  siteEvalStats?: SiteEvalStats;
  isLoading?: boolean;
  visibleModuleIds?: string[];
}

const SKELETON_COUNT = 8;

const SkeletonCard: React.FC<{ pulseAnim: Animated.Value }> = ({ pulseAnim }) => (
  <Animated.View style={[styles.card, styles.skeletonCard, { opacity: pulseAnim }]}>
    <View style={styles.skeletonLabel} />
    <View style={styles.skeletonCount} />
    <View style={styles.skeletonSubtitle} />
  </Animated.View>
);

const KfaModuleSummaryCards: React.FC<KfaModuleSummaryCardsProps> = ({ onModuleSelect, stats, obsStats, rackingStats, nhoStats, siteEvalStats, isLoading, visibleModuleIds }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  useEffect(() => {
    if (isLoading) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, { toValue: 0.4, duration: 700, useNativeDriver: true }),
          Animated.timing(pulseAnim, { toValue: 1, duration: 700, useNativeDriver: true }),
        ])
      ).start();
    } else {
      pulseAnim.stopAnimation();
      pulseAnim.setValue(1);
    }
  }, [isLoading, pulseAnim]);

  const totalEnabled = (stats?.totalShiftHandoverEnabled ?? 0)
    + (stats?.totalLeadershipWalkEnabled ?? 0)
    + (stats?.totalSparcWalkEnabled ?? 0);
  const totalDisabled = (stats?.totalShiftHandoverDisabled ?? 0)
    + (stats?.totalLeadershipWalkDisabled ?? 0)
    + (stats?.totalSparcWalkDisabled ?? 0);

  const MODULES: ModuleStat[] = isDark
    ? [
        {
          id: 'all',
          label: 'ALL MODULES',
          count: stats?.totalElements ?? 0,
          subtitle: `${totalEnabled} Enabled · ${totalDisabled} Disabled`,
          bg: '#1A365D',
          labelColor: '#FFFFFF',
          countColor: '#FFFFFF',
          subtitleColor: 'rgba(255,255,255,0.75)',
        },
        {
          id: 'observations',
          label: 'OBSERVATIONS',
          count: obsStats?.totalElements ?? 0,
          subtitle: `${obsStats?.totalEnabled ?? 0} Enabled · ${obsStats?.totalDisabled ?? 0} Disabled`,
          bg: 'rgba(61,139,255,0.18)',
          labelColor: '#99CFFF',
          countColor: '#FFFFFF',
          subtitleColor: 'rgba(255,255,255,0.7)',
          activeBg: 'rgba(61,139,255,0.30)',
        },
        {
          id: 'shift-handover',
          label: 'SHIFT HANDOVER',
          count: stats?.totalShiftHandover ?? 0,
          subtitle: `${stats?.totalShiftHandoverEnabled ?? 0} Enabled · ${stats?.totalShiftHandoverDisabled ?? 0} Disabled`,
          bg: 'rgba(61,139,255,0.18)',
          labelColor: '#99CFFF',
          countColor: '#FFFFFF',
          subtitleColor: 'rgba(255,255,255,0.7)',
          activeBg: 'rgba(61,139,255,0.30)',
        },
        {
          id: 'leadership-walk',
          label: 'LEADERSHIP WALK',
          count: stats?.totalLeadershipWalk ?? 0,
          subtitle: `${stats?.totalLeadershipWalkEnabled ?? 0} Enabled · ${stats?.totalLeadershipWalkDisabled ?? 0} Disabled`,
          bg: 'rgba(56,182,56,0.18)',
          labelColor: '#8FEBA8',
          countColor: '#FFFFFF',
          subtitleColor: 'rgba(255,255,255,0.7)',
          activeBg: 'rgba(56,182,56,0.30)',
        },
        {
          id: 'sparc-walk',
          label: 'SPARC WALK',
          count: stats?.totalSparcWalk ?? 0,
          subtitle: `${stats?.totalSparcWalkEnabled ?? 0} Enabled · ${stats?.totalSparcWalkDisabled ?? 0} Disabled`,
          bg: 'rgba(124,92,216,0.18)',
          labelColor: '#C4B5FD',
          countColor: '#FFFFFF',
          subtitleColor: 'rgba(255,255,255,0.7)',
          activeBg: 'rgba(124,92,216,0.30)',
        },
        {
          id: 'nho',
          label: 'NHO',
          count: nhoStats?.totalElements ?? 0,
          subtitle: `${nhoStats?.totalEnabled ?? 0} Enabled · ${nhoStats?.totalDisabled ?? 0} Disabled`,
          bg: 'rgba(217,150,0,0.18)',
          labelColor: '#FFD166',
          countColor: '#FFFFFF',
          subtitleColor: 'rgba(255,255,255,0.7)',
          activeBg: 'rgba(217,150,0,0.30)',
        },
        {
          id: 'racking-inspection',
          label: 'RACKING INSPECTION',
          count: rackingStats?.totalElements ?? 0,
          subtitle: `${rackingStats?.totalEnabled ?? 0} Enabled · ${rackingStats?.totalDisabled ?? 0} Disabled`,
          bg: 'rgba(147,51,234,0.18)',
          labelColor: '#D8B4FE',
          countColor: '#FFFFFF',
          subtitleColor: 'rgba(255,255,255,0.7)',
          activeBg: 'rgba(147,51,234,0.30)',
        },
        {
          id: 'site-evaluation',
          label: 'SITE EVALUATION',
          count: siteEvalStats?.totalCount ?? 0,
          subtitle: `${siteEvalStats?.totalEnabled ?? 0} Enabled · ${siteEvalStats?.totalDisabled ?? 0} Disabled`,
          bg: 'rgba(220,53,53,0.18)',
          labelColor: '#FFB09A',
          countColor: '#FFFFFF',
          subtitleColor: 'rgba(255,255,255,0.7)',
          activeBg: 'rgba(220,53,53,0.30)',
        },
      ]
    : [
        {
          id: 'all',
          label: 'ALL MODULES',
          count: stats?.totalElements ?? 0,
          subtitle: `${totalEnabled} Enabled · ${totalDisabled} Disabled`,
          bg: '#01346F',
          labelColor: '#FFFFFF',
          countColor: '#FFFFFF',
          subtitleColor: 'rgba(255,255,255,0.75)',
        },
        {
          id: 'observations',
          label: 'OBSERVATIONS',
          count: obsStats?.totalElements ?? 0,
          subtitle: `${obsStats?.totalEnabled ?? 0} Enabled · ${obsStats?.totalDisabled ?? 0} Disabled`,
          bg: '#EBF5FF',
          labelColor: '#005CBC',
          countColor: '#01346F',
          subtitleColor: '#616161',
          activeBg: '#DAEEFA',
        },
        {
          id: 'shift-handover',
          label: 'SHIFT HANDOVER',
          count: stats?.totalShiftHandover ?? 0,
          subtitle: `${stats?.totalShiftHandoverEnabled ?? 0} Enabled · ${stats?.totalShiftHandoverDisabled ?? 0} Disabled`,
          bg: '#EBF5FF',
          labelColor: '#005CBC',
          countColor: '#01346F',
          subtitleColor: '#616161',
          activeBg: '#DAEEFA',
        },
        {
          id: 'leadership-walk',
          label: 'LEADERSHIP WALK',
          count: stats?.totalLeadershipWalk ?? 0,
          subtitle: `${stats?.totalLeadershipWalkEnabled ?? 0} Enabled · ${stats?.totalLeadershipWalkDisabled ?? 0} Disabled`,
          bg: '#DCFCE7',
          labelColor: '#166534',
          countColor: '#166534',
          subtitleColor: '#616161',
          activeBg: '#BBF7D0',
        },
        {
          id: 'sparc-walk',
          label: 'SPARC WALK',
          count: stats?.totalSparcWalk ?? 0,
          subtitle: `${stats?.totalSparcWalkEnabled ?? 0} Enabled · ${stats?.totalSparcWalkDisabled ?? 0} Disabled`,
          bg: '#F0F4FF',
          labelColor: '#2D2D2D',
          countColor: '#2D2D2D',
          subtitleColor: '#616161',
          activeBg: '#E2E8FF',
        },
        {
          id: 'nho',
          label: 'NHO',
          count: nhoStats?.totalElements ?? 0,
          subtitle: `${nhoStats?.totalEnabled ?? 0} Enabled · ${nhoStats?.totalDisabled ?? 0} Disabled`,
          bg: '#FEFCE8',
          labelColor: '#92400E',
          countColor: '#92400E',
          subtitleColor: '#616161',
          activeBg: '#FEF08A',
        },
        {
          id: 'racking-inspection',
          label: 'RACKING INSPECTION',
          count: rackingStats?.totalElements ?? 0,
          subtitle: `${rackingStats?.totalEnabled ?? 0} Enabled · ${rackingStats?.totalDisabled ?? 0} Disabled`,
          bg: '#EDE9FE',
          labelColor: '#5B21B6',
          countColor: '#5B21B6',
          subtitleColor: '#616161',
          activeBg: '#DDD6FE',
        },
        {
          id: 'site-evaluation',
          label: 'SITE EVALUATION',
          count: siteEvalStats?.totalCount ?? 0,
          subtitle: `${siteEvalStats?.totalEnabled ?? 0} Enabled · ${siteEvalStats?.totalDisabled ?? 0} Disabled`,
          bg: '#FEE2E2',
          labelColor: '#991B1B',
          countColor: '#991B1B',
          subtitleColor: '#616161',
          activeBg: '#FECACA',
        },
      ];

  const handlePress = (id: string) => {
    setSelectedId(id);
    onModuleSelect?.(id);
  };

  const visibleModules = visibleModuleIds
    ? MODULES.filter((m) => visibleModuleIds.includes(m.id))
    : MODULES;

  if (isLoading) {
    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.row}
      >
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <SkeletonCard key={i} pulseAnim={pulseAnim} />
        ))}
      </ScrollView>
    );
  }

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.row}
    >
      {visibleModules.map((module) => {
        const isSelected = selectedId === module.id;
        const bgColor =
          module.id === 'all'
            ? module.bg
            : isSelected && module.activeBg
            ? module.activeBg
            : module.bg;

        return (
          <Pressable
            key={module.id}
            onPress={() => handlePress(module.id)}
            style={[
              styles.card,
              { backgroundColor: bgColor },
              isDark && { borderColor: 'rgba(255,255,255,0.12)', borderWidth: 1 },
              isSelected && module.id !== 'all' && styles.cardSelected,
            ]}
          >
            <Text style={[styles.moduleLabel, { color: module.labelColor }]}>
              {module.label}
            </Text>
            <Text style={[styles.moduleCount, { color: module.countColor }]}>
              {module.count}
            </Text>
            <Text style={[styles.moduleSubtitle, { color: module.subtitleColor }]}>
              {module.subtitle}
            </Text>
          </Pressable>
        );
      })}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: 12,
    paddingBottom: 4,
    marginTop: 16,
    marginBottom: 20,
  },
  card: {
    borderRadius: 10,
    paddingVertical: 16,
    paddingHorizontal: 18,
    minWidth: 148,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 2,
  },
  skeletonCard: {
    backgroundColor: '#E2E8F0',
  },
  skeletonLabel: {
    height: 10,
    borderRadius: 4,
    backgroundColor: '#CBD5E1',
    marginBottom: 12,
    width: '70%',
  },
  skeletonCount: {
    height: 30,
    borderRadius: 4,
    backgroundColor: '#CBD5E1',
    marginBottom: 8,
    width: '40%',
  },
  skeletonSubtitle: {
    height: 10,
    borderRadius: 4,
    backgroundColor: '#CBD5E1',
    width: '80%',
  },
  cardSelected: {
    shadowOpacity: 0.12,
    shadowRadius: 6,
    elevation: 4,
  },
  moduleLabel: {
    fontSize: 11,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  moduleCount: {
    fontSize: 30,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    lineHeight: 36,
    marginBottom: 4,
  },
  moduleSubtitle: {
    fontSize: 12,
    fontWeight: '400',
    fontFamily: FontFamily.regular,
    lineHeight: 16,
  },
});

export default KfaModuleSummaryCards;
